/**
 * Осцилляторы: RSI, MACD, Stochastic, CCI — классика импульса.
 *
 * Осциллятор колеблется в границах и показывает скорость движения.
 * Сигналы у них частые, поэтому почти у каждой идеи есть фильтр тренда —
 * без него половина сигналов будет против рынка.
 */
import { above, adx, below, close, ema, ind, level, macdLine, macdSignal, mirror, number, rsi, rvol, spec, stochK, vwap, weight, } from './kit.js';
export const OSCILLATOR_TEMPLATES = [
    {
        id: 'rsi-extreme',
        name: 'RSI: выход из крайности',
        icon: '📐',
        category: 'indicators',
        popular: true,
        hint: 'Самая известная идея: RSI выходит из зоны перепроданности или перекупленности.',
        steps: ['RSI пересёк границу зоны', 'Цена по нужную сторону от EMA 200'],
        keywords: ['rsi', 'перепроданность', 'перекупленность'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'RSI меряет, насколько быстро росла цена, от 0 до 100. Ниже 30 — перепродано, выше 70 — перекуплено. Выход из зоны — сигнал.',
            why: 'Резкое движение редко продолжается бесконечно: после перегиба цена хотя бы частично возвращается.',
            fails: 'В сильном тренде RSI может неделями держаться выше 70 — и каждая «продажа» будет против рынка.',
            tip: 'Берите сигналы по направлению EMA 200 — усилитель уже стоит в идее.',
        },
        build: (o) => spec(o, 'RSI: выход из крайности', mirror((side) => ({
            context: [],
            trigger: side.cross(rsi, number(level(side, 30))),
            confirmations: [weight('c1', side.beyond(close, ema(200)), 100)],
        })), 0),
    },
    {
        id: 'macd-cross',
        name: 'MACD: пересечение',
        icon: '📐',
        category: 'indicators',
        hint: 'Линия MACD пересекает сигнальную — классическая смена импульса.',
        steps: ['MACD пересёк сигнальную', 'Крупный тренд согласен', 'Тренд силён'],
        keywords: ['macd', 'пересечение'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'MACD — разница двух средних. Когда он пересекает свою сигнальную линию, импульс меняет направление.',
            why: 'Пересечение отмечает момент, когда ускорение сменилось на замедление или наоборот.',
            fails: 'В боковике пересечений очень много и почти все ложные.',
            tip: 'Для более строгой версии смотрите «MACD + EMA 200» в классике.',
        },
        build: (o) => spec(o, 'MACD: пересечение', mirror((side) => ({
            context: [],
            trigger: side.cross(macdLine, macdSignal),
            confirmations: [weight('c1', side.beyond(close, ema(200)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 60),
    },
    {
        id: 'stochastic',
        name: 'Stochastic: выход из зоны',
        icon: '📐',
        category: 'indicators',
        hint: 'Быстрый осциллятор выходит из крайней зоны по ходу тренда.',
        steps: ['Цена по нужную сторону от EMA 50', '%K пересёк границу зоны', 'Объём'],
        keywords: ['stochastic', 'стохастик'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Stochastic показывает, где закрылась цена внутри диапазона последних баров. Выход из зоны 20/80 по ходу тренда — конец отката.',
            why: 'Быстрый осциллятор хорошо ловит короткие откаты внутри тренда.',
            fails: 'Сигналов очень много; без фильтра тренда большинство — шум.',
            tip: 'Лучше всего работает на младших таймфреймах как вход по тренду старшего.',
        },
        build: (o) => spec(o, 'Stochastic: выход из зоны', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.cross(stochK, number(level(side, 20))),
            confirmations: [weight('c1', above(rvol, number(1.1)), 100)],
        })), 0),
    },
    {
        id: 'cci-extreme',
        name: 'CCI: выход из крайности',
        icon: '📐',
        category: 'indicators',
        hint: 'CCI возвращается из зоны за ±100 — растяжение закончилось.',
        steps: ['CCI пересёк ±100', 'Объём'],
        keywords: ['cci'],
        passport: {
            level: 'skilled',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'CCI показывает, насколько цена отошла от своей средней. За ±100 — далеко; возврат — растяжение закончилось.',
            why: 'Как и RSI, ловит перегибы, но чувствительнее к резким движениям.',
            fails: 'В тренде CCI уходит за 200–300 и долго там держится.',
            tip: 'Работает в спокойном рынке; при сильном тренде лучше выключить.',
        },
        build: (o) => spec(o, 'CCI: выход из крайности', mirror((side) => ({
            context: [],
            trigger: side.cross(ind('cci', { period: 20 }), number(side.sign > 0 ? -100 : 100)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 60), weight('c2', below(adx, number(25)), 40)],
        })), 0),
    },
    {
        id: 'vwap-reclaim',
        name: 'Возврат к VWAP',
        icon: '📐',
        category: 'indicators',
        hint: 'Цена возвращается к средней по объёму — уровню, вокруг которого торгуют.',
        steps: ['Цена пересекла VWAP', 'Объём подтверждает'],
        keywords: ['vwap', 'средняя по объёму'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'VWAP — средняя цена сделок с учётом объёма. Цена выше — покупатели сильнее, ниже — продавцы.',
            why: 'Пересечение VWAP на объёме показывает, что сторона сменилась в пользу нового движения.',
            fails: 'Около VWAP цена часто топчется, пересекая её туда-обратно.',
            tip: 'Объём на пересечении — обязательный фильтр: без него сигналов втрое больше.',
        },
        build: (o) => spec(o, 'Возврат к VWAP', mirror((side) => ({
            context: [],
            trigger: side.cross(close, vwap),
            confirmations: [weight('c1', above(rvol, number(1.2)), 100)],
        })), 100),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'rsi-50',
        name: 'RSI: смена настроения',
        icon: '📐',
        category: 'indicators',
        hint: 'RSI пересекает середину — 50 — по ходу крупного тренда: импульс снова на нашей стороне.',
        steps: ['Цена по нужную сторону от EMA 200', 'RSI пересёк 50', 'Тренд силён'],
        keywords: ['rsi', '50', 'середина', 'импульс'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'RSI выше 50 — покупатели сильнее, ниже — продавцы. Пересечение середины по ходу тренда — конец отката.',
            why: 'Середина RSI — граница настроения: в растущем рынке откаты заканчиваются около неё.',
            fails: 'В боковике RSI крутится около 50 и пересекает её постоянно.',
            tip: 'Фильтр ADX здесь важнее всего: он отсекает боковые пересечения.',
        },
        build: (o) => spec(o, 'RSI: смена настроения', mirror((side) => ({
            context: [side.beyond(close, ema(200)), above(adx, number(18))],
            trigger: side.cross(rsi, number(50)),
            confirmations: [weight('c1', above(adx, number(25)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=oscillators.js.map